import { Flex, Select, Text, useColorModeValue } from '@chakra-ui/react';
import { Landmark, Wallet } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { OktonChart as Chart } from '@okton/platform-ui/charts';
import { ContaBancaria } from '../../../services/hooks/useContasBancarias';
import {
  DadosEvolucao,
  FiltrosDashboard,
  useDashboardEvolucao,
} from '../../../services/hooks/useDashboard';
import { EstadoRecurso } from '../../../services/hooks/useDashboardPainel';
import formatValue from '../../../utils/formatValue';
import { rotuloEixo } from '../formatacao';
import CargaGrafico from './CargaGrafico';
import ChartInfo from './ChartInfo';
import { useCoresDashboard } from './cores';

interface Props {
  filtros: FiltrosDashboard;
  contas: ContaBancaria[];
  evolucaoGeral: EstadoRecurso<DadosEvolucao>;
}

const TITULO = 'Evolução do Saldo Bancário';
const ALTURA = '420px';

/**
 * O seletor de Conta daqui é só deste gráfico: comparar o saldo de uma Conta
 * com o resto da tela não deve obrigar a refiltrar o Dashboard inteiro. Ele
 * acompanha o filtro geral sempre que este muda.
 */
const SaldoChart: React.FC<Props> = ({ filtros, contas, evolucaoGeral }) => {
  const cores = useCoresDashboard();
  const color = useColorModeValue('gray.700', 'gray.100');
  const filtroGeral = JSON.stringify(filtros);
  const [selecao, setSelecao] = useState({ filtroGeral, conta: filtros.conta });
  // Retire the previous local selection, including when returning A → B → A.
  useEffect(() => {
    setSelecao(current =>
      current.filtroGeral === filtroGeral
        ? current
        : { filtroGeral, conta: filtros.conta },
    );
  }, [filtroGeral, filtros.conta]);
  // Derive the effective selection during render. An effect would issue one
  // transient query with the old local account when the general filter changes.
  const conta =
    selecao.filtroGeral === filtroGeral ? selecao.conta : filtros.conta;
  const evolucaoLocal = useDashboardEvolucao(
    { ...filtros, conta },
    { enabled: conta !== filtros.conta },
  );
  const { data, isLoading, isError, error, isPreviousData, refetch } =
    conta === filtros.conta ? evolucaoGeral : evolucaoLocal;

  const pontos = data?.rows ?? [];
  const agrupamento = data?.agrupamento ?? 'dia';
  const saldoInicial = Number(data?.saldoinicial ?? 0);
  const saldoFinal = pontos.length
    ? Number(pontos[pontos.length - 1].saldo)
    : saldoInicial;
  // Um dia só: o saldo ao fim de cada hora; o último ponto é o saldo do dia.
  const horas = data?.temhora ? data.horas : null;
  const serie = useMemo(
    () =>
      horas
        ? horas.map(p => ({
            periodo: p.hora,
            Saldo: Number(p.saldo),
          }))
        : (data?.rows ?? []).map(p => ({
            periodo: rotuloEixo(p.periodo, agrupamento),
            Saldo: Number(p.saldo),
          })),
    [horas, data, agrupamento],
  );

  return (
    <CargaGrafico
      atualizando={isPreviousData}
      titulo={TITULO}
      isLoading={isLoading}
      isError={isError}
      error={error}
      onRetry={refetch}
      h={ALTURA}
    >
      <Chart
        h={ALTURA}
        title={
          <Flex align="center" gridGap="3" flexWrap="wrap">
            <Text as="span">{TITULO}</Text>
            <Select
              size="sm"
              rounded="lg"
              w="190px"
              // O body do tema é branco: sem cor explícita o texto some no
              // claro.
              color={color}
              value={conta}
              onChange={e => setSelecao({ filtroGeral, conta: e.target.value })}
              aria-label="Conta do gráfico de saldo"
            >
              <option value="">Todas as contas</option>
              {contas.map(c => (
                <option key={c.id_cts} value={c.id_cts}>
                  {c.descricao_cts}
                </option>
              ))}
            </Select>
          </Flex>
        }
        data={serie}
        format="area"
        formatosPermitidos={['area', 'bar', 'line']}
        graphColor={[cores.saldo]}
        dataKeys={['Saldo']}
        nameKey="periodo"
        isMoneyValue
        rightInfo={
          <Flex flexDir="column" w="30%" gridGap="2" ml="4">
            <ChartInfo
              titulo="Saldo inicial"
              valor={formatValue(saldoInicial)}
              corValor={cores.saldo}
              icon={<Wallet size={20} color={cores.saldo} />}
            />
            <ChartInfo
              titulo="Saldo final"
              valor={formatValue(saldoFinal)}
              corValor={
                saldoFinal >= saldoInicial ? cores.conciliado : cores.divergente
              }
              icon={<Landmark size={20} color={cores.saldo} />}
            />
          </Flex>
        }
      />
    </CargaGrafico>
  );
};

export default SaldoChart;
